// ECHOLOOP renderer — "chrono-lab hologram" look, Canvas 2D.
// Deep violet field, parallax glyph grid, glowing slab tiles, energy doors,
// a luminous runner and his translucent magenta past lives.

import type { ParsedLevel, Echo, DoorMode } from './types';
import { TILE, VIEW_W, VIEW_H } from './types';
import { Sim, PLAYER_W, PLAYER_H } from './sim';

// ---- particles -------------------------------------------------------------

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  gravity: number;
}

const particles: Particle[] = [];

export function spawnBurst(
  x: number,
  y: number,
  color: string,
  count: number,
  speed: number,
  gravity = 600,
): void {
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const s = speed * (0.3 + Math.random() * 0.7);
    particles.push({
      x,
      y,
      vx: Math.cos(a) * s,
      vy: Math.sin(a) * s - speed * 0.3,
      life: 0,
      maxLife: 0.4 + Math.random() * 0.5,
      size: 1.5 + Math.random() * 2.5,
      color,
      gravity,
    });
  }
}

export function updateParticles(dt: number): void {
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i]!;
    p.life += dt;
    if (p.life >= p.maxLife) {
      particles.splice(i, 1);
      continue;
    }
    p.vy += p.gravity * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
  }
}

// ---- trail (afterimages) -----------------------------------------------------

export interface TrailPoint {
  x: number;
  y: number;
  age: number;
}

export function pushTrail(trail: TrailPoint[], x: number, y: number): void {
  trail.push({ x, y, age: 0 });
  if (trail.length > 14) trail.shift();
}

export function ageTrail(trail: TrailPoint[], dt: number): void {
  for (const t of trail) t.age += dt;
}

// ---- helpers ---------------------------------------------------------------

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

const DOOR_HUES: Record<string, number> = { A: 190, B: 38, C: 315, D: 265 };

// Hue for the timed-seal accent — a hot amber→red signature distinct from every
// door hue above, so a momentary seal never reads like a normal hold/toggle gate.
const SEAL_HUE = 8;

/** Button ids that arm the exit seal — the pad(s) the seal door `needs`. Empty
 *  for legacy (unsealed) levels, so their pads render exactly as before. */
function sealPadButtonIds(level: ParsedLevel): Set<number> {
  const seal = level.def.seal;
  if (seal === undefined) return new Set();
  const door = level.doors.find((d) => d.letter === seal);
  return new Set(door ? door.needs : []);
}

// ---- backdrop ----------------------------------------------------------------

// The vertical field gradient is static (VIEW_W/H are fixed) — build it once
// and reuse it every frame instead of reallocating a CanvasGradient per RAF.
let fieldGrad: CanvasGradient | null = null;
function backdropGradient(ctx: CanvasRenderingContext2D): CanvasGradient {
  if (fieldGrad === null) {
    const g = ctx.createLinearGradient(0, 0, 0, VIEW_H);
    g.addColorStop(0, '#0d0719');
    g.addColorStop(0.55, '#0a0613');
    g.addColorStop(1, '#06040c');
    fieldGrad = g;
  }
  return fieldGrad;
}

export function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  level: ParsedLevel,
  camX: number,
  time: number,
): void {
  ctx.fillStyle = backdropGradient(ctx);
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);

  // deepest layer — slow-drifting data columns (parallax 0.12) give the void
  // a sense of depth + constant gentle motion behind everything.
  ctx.save();
  ctx.fillStyle = '#5a4f86';
  const colStep = 132;
  const colOx = -((camX * 0.12 + time * 5) % colStep);
  for (let x = colOx - colStep; x < VIEW_W + colStep; x += colStep) {
    const k = (x * 0.013 + 1) % 1;
    const h = VIEW_H * (0.34 + 0.4 * (0.5 + 0.5 * Math.sin(x * 0.7)));
    const y = VIEW_H * 0.5 - h / 2 + Math.sin(time * 0.3 + x) * 10;
    ctx.globalAlpha = 0.018 + 0.02 * (0.5 + 0.5 * Math.sin(time * 0.6 + x * 0.5));
    ctx.fillRect(x, y, 2 + (k > 0.5 ? 2 : 0), h);
  }
  ctx.restore();

  // giant faint chrono-ring, parallax 0.2
  const cx = VIEW_W * 0.72 - camX * 0.2;
  const cy = VIEW_H * 0.36;
  ctx.save();
  ctx.globalAlpha = 0.06;
  ctx.strokeStyle = level.def.accent;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, 190, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([4, 22]);
  ctx.lineWidth = 14;
  ctx.beginPath();
  ctx.arc(cx, cy, 160, time * 0.07, time * 0.07 + Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // light nodes orbiting the ring — small bright travellers that keep the
  // background feeling powered-on and in motion.
  ctx.save();
  for (let i = 0; i < 3; i++) {
    const a = time * (0.22 + i * 0.05) + (i * Math.PI * 2) / 3;
    const rr = 160 + (i - 1) * 30;
    const nx = cx + Math.cos(a) * rr;
    const ny = cy + Math.sin(a) * rr;
    ctx.globalAlpha = 0.16 + 0.08 * Math.sin(time * 2 + i);
    ctx.fillStyle = i === 1 ? '#f4f8ff' : level.def.accent;
    ctx.beginPath();
    ctx.arc(nx, ny, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // parallax dot grid, 0.5
  ctx.save();
  ctx.globalAlpha = 0.1;
  ctx.fillStyle = '#8a7fb8';
  const step = 56;
  const ox = -((camX * 0.5) % step);
  const oy = (time * 6) % step;
  for (let x = ox - step; x < VIEW_W + step; x += step) {
    for (let y = oy - step; y < VIEW_H + step; y += step) {
      ctx.fillRect(x, y, 1.5, 1.5);
    }
  }
  ctx.restore();

  // drifting motes
  ctx.save();
  for (let i = 0; i < 30; i++) {
    const seed = i * 97.31;
    const mx = ((seed * 13.7 + time * (6 + (i % 5) * 3)) % (VIEW_W + 80)) - 40;
    const my = (seed * 7.3) % VIEW_H;
    const tw = 0.5 + 0.5 * Math.sin(time * 1.4 + seed);
    ctx.globalAlpha = 0.05 + tw * 0.08;
    ctx.fillStyle = i % 3 === 0 ? level.def.accent : '#b7a9e8';
    ctx.beginPath();
    ctx.arc(mx, my, 1 + (i % 3), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // slow rising sparks — a few embers of spent time drifting upward.
  ctx.save();
  for (let i = 0; i < 9; i++) {
    const seed = i * 51.7;
    const sx = (seed * 19.3) % VIEW_W;
    const sy = VIEW_H - ((seed * 11.1 + time * (22 + (i % 4) * 8)) % (VIEW_H + 40)) + 20;
    const tw = 0.5 + 0.5 * Math.sin(time * 2.2 + seed);
    ctx.globalAlpha = 0.04 + tw * 0.1;
    ctx.fillStyle = i % 2 === 0 ? level.def.accent : '#ffffff';
    ctx.fillRect(sx, sy, 1.5, 1.5 + tw * 2);
  }
  ctx.restore();

  // a soft vertical light sweep crossing on a long cycle — subtle "scan".
  const sweepCycle = 11;
  const phase = (time % sweepCycle) / sweepCycle;
  if (phase < 0.5) {
    const sweepX = phase * 2 * (VIEW_W + 160) - 80;
    const sg = ctx.createLinearGradient(sweepX - 60, 0, sweepX + 60, 0);
    sg.addColorStop(0, 'transparent');
    sg.addColorStop(0.5, `${level.def.accent}14`);
    sg.addColorStop(1, 'transparent');
    ctx.fillStyle = sg;
    ctx.fillRect(sweepX - 60, 0, 120, VIEW_H);
  }
}

// ---- world -------------------------------------------------------------------

export function drawWorld(
  ctx: CanvasRenderingContext2D,
  sim: Sim,
  camX: number,
  time: number,
): void {
  const level = sim.level;
  ctx.save();
  ctx.translate(-camX, 0);

  // ambient play-space dust — faint motes that always drift around the runner,
  // so even an empty stretch of level feels alive. World-space + view-anchored,
  // allocation-free (seed/time loop).
  ctx.save();
  for (let i = 0; i < 22; i++) {
    const seed = i * 73.7;
    const wx = camX + ((seed * 13.3) % (VIEW_W + 80)) - 40;
    const wy = (((seed * 7.7 - time * (10 + (i % 4) * 6)) % (VIEW_H + 60)) + (VIEW_H + 60)) % (VIEW_H + 60) - 30;
    const tw = 0.5 + 0.5 * Math.sin(time * 1.8 + seed);
    ctx.globalAlpha = 0.04 + tw * 0.06;
    ctx.fillStyle = i % 4 === 0 ? level.def.accent : '#9b8fd0';
    ctx.beginPath();
    ctx.arc(wx, wy, 0.8 + (i % 2), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  const c0 = Math.max(0, Math.floor(camX / TILE) - 1);
  const c1 = Math.min(level.cols - 1, Math.ceil((camX + VIEW_W) / TILE) + 1);

  // --- tiles
  for (let r = 0; r < level.rows; r++) {
    for (let c = c0; c <= c1; c++) {
      const ch = level.tiles[r]![c]!;
      const x = c * TILE;
      const y = r * TILE;
      if (ch === '#') {
        drawSlab(ctx, level, x, y, r, c);
      } else if (ch === '-') {
        drawOneWay(ctx, level, x, y, time);
      } else if (ch === '^') {
        drawSpikes(ctx, x, y, time);
      } else if (ch === '*') {
        drawSpring(ctx, x, y, time);
      } else if (ch === 'x') {
        drawCrumble(ctx, sim, x, y, r, c, time, level);
      } else if (ch >= 'A' && ch <= 'D') {
        const door = level.doors.find((d) => d.letter === ch);
        drawDoor(
          ctx,
          ch,
          x,
          y,
          sim.openDoors.has(ch),
          time,
          level,
          r,
          door?.mode ?? 'hold',
          door?.mode === 'timed' ? sim.timedDoorFrac(ch) : 0,
        );
      }
    }
  }

  // --- buttons (the seal pad gets a marker so pad→exit reads at a glance)
  const sealPadIds = sealPadButtonIds(level);
  for (const b of sim.level.buttons) {
    drawButton(
      ctx,
      b.col * TILE,
      b.row * TILE,
      sim.pressedButtons.has(b.id),
      time,
      level,
      sealPadIds.has(b.id),
      sim.exitArmed,
    );
  }

  // --- levers (toggle-door switches)
  for (const lv of sim.level.levers) {
    drawLever(ctx, lv.col * TILE, lv.row * TILE, sim.toggledOn.has(lv.letter), time, level);
  }

  // --- exit portal (sealed exits render locked/dim vs. armed/pulsing-bright)
  drawPortal(
    ctx,
    level.exit.col * TILE,
    level.exit.row * TILE,
    time,
    level,
    level.def.seal !== undefined,
    sim.exitArmed,
  );

  // --- echoes (under the player)
  for (const e of sim.echoes) {
    drawEcho(ctx, sim, e, time);
  }

  // --- player
  if (!sim.dead) {
    drawRunner(
      ctx,
      sim.px,
      sim.py,
      sim.face,
      sim.vy,
      sim.grounded,
      time,
      '#f4f8ff',
      level.def.accent,
      1,
    );
  }

  // --- particles
  for (const p of particles) {
    const a = 1 - p.life / p.maxLife;
    ctx.globalAlpha = a;
    ctx.fillStyle = p.color;
    ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
  }
  ctx.globalAlpha = 1;

  ctx.restore();
}

function drawSlab(
  ctx: CanvasRenderingContext2D,
  level: ParsedLevel,
  x: number,
  y: number,
  r: number,
  c: number,
): void {
  ctx.fillStyle = '#161126';
  ctx.fillRect(x, y, TILE, TILE);
  // subtle inner texture
  ctx.fillStyle = 'rgba(255,255,255,0.018)';
  ctx.fillRect(x + 2, y + 2, TILE - 4, TILE - 4);
  // exposed-top accent line
  const above = r > 0 ? level.tiles[r - 1]![c]! : '.';
  if (above !== '#' && !(above >= 'A' && above <= 'D')) {
    ctx.fillStyle = level.def.accent2;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(x, y, TILE, 2.5);
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = level.def.accent;
    ctx.fillRect(x, y + 2.5, TILE, 5);
    ctx.globalAlpha = 1;
  }
}

function drawOneWay(
  ctx: CanvasRenderingContext2D,
  level: ParsedLevel,
  x: number,
  y: number,
  time: number,
): void {
  ctx.fillStyle = 'rgba(22, 17, 38, 0.9)';
  ctx.fillRect(x, y, TILE, 9);
  ctx.fillStyle = level.def.accent;
  ctx.globalAlpha = 0.7 + 0.2 * Math.sin(time * 3 + x * 0.05);
  ctx.fillRect(x + 2, y, TILE - 4, 2.5);
  ctx.globalAlpha = 0.35;
  for (let i = 0; i < 3; i++) {
    ctx.fillRect(x + 4 + i * 9, y + 5, 5, 1.5);
  }
  ctx.globalAlpha = 1;
}

function drawSpikes(ctx: CanvasRenderingContext2D, x: number, y: number, time: number): void {
  const pulse = 0.65 + 0.35 * Math.sin(time * 5 + x * 0.3);
  ctx.fillStyle = '#2a1020';
  ctx.fillRect(x, y + TILE * 0.4, TILE, TILE * 0.6);
  ctx.fillStyle = `rgba(255, 70, 90, ${0.75 * pulse})`;
  const n = 3;
  const w = TILE / n;
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    ctx.moveTo(x + i * w, y + TILE);
    ctx.lineTo(x + i * w + w / 2, y + TILE * 0.3);
    ctx.lineTo(x + (i + 1) * w, y + TILE);
  }
  ctx.fill();
  ctx.strokeStyle = `rgba(255, 140, 150, ${pulse})`;
  ctx.lineWidth = 1;
  ctx.stroke();
}

// Spring pad — a mint coil that reads as "launch": distinct hue from every
// level accent so it's instantly recognizable as the bounce tile.
function drawSpring(ctx: CanvasRenderingContext2D, x: number, y: number, time: number): void {
  const pulse = 0.55 + 0.45 * Math.sin(time * 6 + x * 0.1);
  // housing (lower portion of the tile)
  ctx.fillStyle = '#13211c';
  ctx.fillRect(x, y + TILE * 0.5, TILE, TILE * 0.5);
  // coil
  ctx.strokeStyle = `rgba(90, 240, 180, ${0.5 + 0.35 * pulse})`;
  ctx.lineWidth = 2;
  ctx.beginPath();
  const baseY = y + TILE - 4;
  for (let i = 0; i < 3; i++) {
    const yy = baseY - i * 5;
    ctx.moveTo(x + 6, yy);
    ctx.lineTo(x + TILE - 6, yy - 2.5);
  }
  ctx.stroke();
  // launch plate
  ctx.fillStyle = `hsl(155, 90%, ${52 + pulse * 12}%)`;
  roundRect(ctx, x + 3, y + TILE * 0.42 - pulse * 1.5, TILE - 6, 6, 3);
  ctx.fill();
  // up chevrons
  ctx.fillStyle = `rgba(180, 255, 220, ${0.35 + 0.5 * pulse})`;
  for (let i = 0; i < 2; i++) {
    const ay = y + TILE * 0.36 - i * 8;
    ctx.beginPath();
    ctx.moveTo(x + TILE / 2, ay);
    ctx.lineTo(x + TILE / 2 - 5, ay + 6);
    ctx.lineTo(x + TILE / 2 + 5, ay + 6);
    ctx.fill();
  }
}

// Crumble slab — warm/orange so it reads as fragile; cracks widen + the tile
// jitters as it nears collapse; a dashed ghost marks where it will reform.
function drawCrumble(
  ctx: CanvasRenderingContext2D,
  sim: Sim,
  x: number,
  y: number,
  r: number,
  c: number,
  time: number,
  level: ParsedLevel,
): void {
  if (sim.brokenCrumbles.has(`${c},${r}`)) {
    ctx.save();
    ctx.globalAlpha = 0.18 + 0.1 * Math.sin(time * 3 + x);
    ctx.strokeStyle = level.def.accent2;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(x + 2.5, y + 2.5, TILE - 5, TILE - 5);
    ctx.setLineDash([]);
    ctx.restore();
    return;
  }
  const frac = sim.crumbleBreakFrac(c, r); // 0 intact → 1 about to fall
  ctx.save();
  if (frac > 0) ctx.translate((Math.random() - 0.5) * frac * 3, (Math.random() - 0.5) * frac * 2);
  ctx.fillStyle = frac > 0 ? `rgb(${Math.round(46 + frac * 46)}, 30, 28)` : '#241a26';
  ctx.fillRect(x, y, TILE, TILE);
  ctx.fillStyle = 'rgba(255,255,255,0.02)';
  ctx.fillRect(x + 2, y + 2, TILE - 4, TILE - 4);
  ctx.fillStyle = frac > 0 ? '#ff9a4b' : '#b98ad0';
  ctx.globalAlpha = 0.85;
  ctx.fillRect(x, y, TILE, 2.5);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = `rgba(255, 160, 90, ${0.28 + 0.6 * frac})`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 6, y + 4);
  ctx.lineTo(x + 12, y + 15);
  ctx.lineTo(x + 9, y + TILE - 4);
  ctx.moveTo(x + TILE - 7, y + 5);
  ctx.lineTo(x + TILE - 14, y + 16);
  ctx.lineTo(x + TILE - 10, y + TILE - 5);
  if (frac > 0.4) {
    ctx.moveTo(x + 12, y + 15);
    ctx.lineTo(x + TILE - 14, y + 16);
  }
  ctx.stroke();
  ctx.restore();
}

// Floor lever for a toggle door — tilts right + glows green when flipped on.
function drawLever(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  on: boolean,
  time: number,
  level: ParsedLevel,
): void {
  const cx = x + TILE / 2;
  const baseY = y + TILE - 5;
  ctx.fillStyle = '#241b3d';
  roundRect(ctx, x + 6, baseY - 4, TILE - 12, 7, 2);
  ctx.fill();
  ctx.fillStyle = '#3a2e5e';
  ctx.beginPath();
  ctx.arc(cx, baseY - 3, 3, 0, Math.PI * 2);
  ctx.fill();
  const ang = on ? -0.7 : 0.7;
  const len = 15;
  const ex = cx + Math.sin(ang) * len;
  const ey = baseY - 3 - Math.cos(ang) * len;
  ctx.strokeStyle = on ? '#5dff9c' : 'rgba(214,226,240,0.6)';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx, baseY - 3);
  ctx.lineTo(ex, ey);
  ctx.stroke();
  ctx.lineCap = 'butt';
  ctx.fillStyle = on ? '#5dff9c' : level.def.accent;
  ctx.globalAlpha = on ? 1 : 0.65 + 0.25 * Math.sin(time * 4 + x);
  ctx.beginPath();
  ctx.arc(ex, ey, 3.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

function drawDoor(
  ctx: CanvasRenderingContext2D,
  letter: string,
  x: number,
  y: number,
  open: boolean,
  time: number,
  level: ParsedLevel,
  row: number,
  mode: DoorMode,
  frac: number,
): void {
  if (mode === 'timed') {
    drawTimedSealDoor(ctx, letter, x, y, open, frac, time, level, row);
    return;
  }

  const hue = DOOR_HUES[letter] ?? 200;
  const isTopCell = row === 0 || level.tiles[row - 1]![Math.floor(x / TILE)] !== letter;

  if (open) {
    // retracted: only the top cell shows a humming emitter stub
    if (isTopCell) {
      ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.9)`;
      roundRect(ctx, x + 4, y, TILE - 8, 7, 3);
      ctx.fill();
      ctx.globalAlpha = 0.25 + 0.15 * Math.sin(time * 8);
      ctx.fillStyle = `hsla(${hue}, 90%, 70%, 1)`;
      ctx.fillRect(x + 7, y + 8, TILE - 14, 4);
      ctx.globalAlpha = 1;
    }
    return;
  }

  // closed: energy field
  const grad = ctx.createLinearGradient(x, 0, x + TILE, 0);
  grad.addColorStop(0, `hsla(${hue}, 85%, 55%, 0.16)`);
  grad.addColorStop(0.5, `hsla(${hue}, 90%, 65%, 0.4)`);
  grad.addColorStop(1, `hsla(${hue}, 85%, 55%, 0.16)`);
  ctx.fillStyle = grad;
  ctx.fillRect(x + 5, y, TILE - 10, TILE);

  // animated scan bands
  ctx.save();
  ctx.beginPath();
  ctx.rect(x + 5, y, TILE - 10, TILE);
  ctx.clip();
  ctx.fillStyle = `hsla(${hue}, 95%, 75%, 0.5)`;
  const bandY = ((time * 60 + y * 0.7) % (TILE * 2)) - 4;
  ctx.fillRect(x + 5, y + bandY, TILE - 10, 3);
  ctx.restore();

  // rails
  ctx.fillStyle = `hsla(${hue}, 70%, 70%, 0.9)`;
  ctx.fillRect(x + 4, y, 2, TILE);
  ctx.fillRect(x + TILE - 6, y, 2, TILE);

  if (isTopCell) {
    ctx.fillStyle = `hsla(${hue}, 80%, 62%, 1)`;
    roundRect(ctx, x + 3, y - 3, TILE - 6, 7, 3);
    ctx.fill();
  }
}

// A momentary TIMED seal. Reads totally differently from a hold/toggle door:
//   - amber/red hue (SEAL_HUE), never a normal door hue
//   - when OPEN, a SHRINKING window bar drains down the cell (frac 1→0) so the
//     player literally watches it about to slam shut; low frac flashes red+urgent
//   - when SHUT, it's a heavy locked grille with a padlock-ish bar + chevrons,
//     clearly "you missed the window / press the pad to re-arm".
function drawTimedSealDoor(
  ctx: CanvasRenderingContext2D,
  letter: string,
  x: number,
  y: number,
  open: boolean,
  frac: number,
  time: number,
  level: ParsedLevel,
  row: number,
): void {
  const isTopCell = row === 0 || level.tiles[row - 1]![Math.floor(x / TILE)] !== letter;

  if (open) {
    // urgency ramps as the window closes: hue slides amber→red, flashes when low.
    const urgent = 1 - frac; // 0 just-armed → 1 about to shut
    const hue = SEAL_HUE + 28 * frac; // ~36 (amber) when fresh → 8 (red) at the end
    const flash = urgent > 0.55 ? 0.5 + 0.5 * Math.sin(time * 24) : 1; // strobe near the end
    const lum = 60 + urgent * 8;

    // a faint emitter haze still fills the gap so you read it as "currently open"
    ctx.save();
    ctx.globalAlpha = 0.1 + 0.12 * frac;
    ctx.fillStyle = `hsla(${hue}, 95%, ${lum}%, 1)`;
    ctx.fillRect(x + 5, y, TILE - 10, TILE);
    ctx.restore();

    // SHRINKING WINDOW BAR — drains from full height down to nothing as frac→0,
    // anchored at the bottom of the cell so it visibly "closes in".
    const barH = Math.max(0, (TILE - 4) * frac);
    const barY = y + (TILE - 2) - barH;
    ctx.save();
    ctx.globalAlpha = (0.55 + 0.4 * urgent) * flash;
    const bg = ctx.createLinearGradient(x, barY, x, barY + barH);
    bg.addColorStop(0, `hsla(${hue}, 95%, ${lum + 12}%, 0.9)`);
    bg.addColorStop(1, `hsla(${hue}, 90%, ${lum - 6}%, 0.5)`);
    ctx.fillStyle = bg;
    roundRect(ctx, x + 6, barY, TILE - 12, barH, 3);
    ctx.fill();
    ctx.restore();

    // rails (always frame the channel)
    ctx.fillStyle = `hsla(${hue}, 85%, 72%, ${0.85 * flash})`;
    ctx.fillRect(x + 4, y, 2, TILE);
    ctx.fillRect(x + TILE - 6, y, 2, TILE);

    // top emitter stub — pulses hot, strobing red when nearly shut
    if (isTopCell) {
      ctx.save();
      ctx.globalAlpha = flash;
      ctx.fillStyle = `hsla(${hue}, 90%, ${lum + 8}%, 1)`;
      roundRect(ctx, x + 3, y - 3, TILE - 6, 7, 3);
      ctx.fill();
      ctx.restore();
    }
    return;
  }

  // SHUT — a heavy locked grille so a closed seal can't be mistaken for an open
  // path. Cool/desaturated red, padlock bar + warning chevrons.
  const lockPulse = 0.4 + 0.12 * Math.sin(time * 3 + y * 0.1);
  ctx.fillStyle = `hsla(${SEAL_HUE}, 45%, 30%, 0.92)`;
  ctx.fillRect(x + 5, y, TILE - 10, TILE);
  // diagonal hazard hatching reads as "locked / do not pass"
  ctx.save();
  ctx.beginPath();
  ctx.rect(x + 5, y, TILE - 10, TILE);
  ctx.clip();
  ctx.strokeStyle = `hsla(${SEAL_HUE}, 60%, 45%, ${lockPulse})`;
  ctx.lineWidth = 2;
  for (let d = -TILE; d < TILE * 2; d += 8) {
    ctx.beginPath();
    ctx.moveTo(x + d, y);
    ctx.lineTo(x + d + TILE, y + TILE);
    ctx.stroke();
  }
  ctx.restore();
  // solid rails
  ctx.fillStyle = `hsla(${SEAL_HUE}, 55%, 55%, 0.9)`;
  ctx.fillRect(x + 4, y, 2.5, TILE);
  ctx.fillRect(x + TILE - 6.5, y, 2.5, TILE);
  // padlock cross-bar on the top cell
  if (isTopCell) {
    ctx.fillStyle = `hsla(${SEAL_HUE}, 60%, 58%, 1)`;
    roundRect(ctx, x + 3, y - 3, TILE - 6, 7, 3);
    ctx.fill();
    // little lock shackle so "SEALED" reads instantly
    ctx.strokeStyle = `hsla(${SEAL_HUE}, 65%, 70%, 0.9)`;
    ctx.lineWidth = 2;
    const lx = x + TILE / 2;
    ctx.beginPath();
    ctx.arc(lx, y + 8, 4, Math.PI, 0);
    ctx.stroke();
    ctx.fillStyle = `hsla(${SEAL_HUE}, 55%, 50%, 1)`;
    roundRect(ctx, lx - 5, y + 8, 10, 8, 2);
    ctx.fill();
  }
}

function drawButton(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  pressed: boolean,
  time: number,
  level: ParsedLevel,
  isSealPad = false,
  exitArmed = false,
): void {
  const padH = pressed ? 5 : 9;
  const baseY = y + TILE - 4;

  // Seal-pad marker: a subtle amber ground-halo + label so the player connects
  // THIS pad to the timed exit seal. Pulses while the exit is armed (live window).
  if (isSealPad) {
    const cx = x + TILE / 2;
    ctx.save();
    const halo = exitArmed ? 0.22 + 0.18 * (0.5 + 0.5 * Math.sin(time * 8)) : 0.12;
    const hg = ctx.createRadialGradient(cx, baseY, 1, cx, baseY, 18);
    hg.addColorStop(0, `hsla(${SEAL_HUE + 24}, 95%, 60%, ${halo})`);
    hg.addColorStop(1, 'transparent');
    ctx.fillStyle = hg;
    ctx.fillRect(cx - 18, baseY - 18, 36, 24);
    ctx.globalAlpha = exitArmed ? 0.9 : 0.5;
    ctx.fillStyle = `hsla(${SEAL_HUE + 24}, 90%, 68%, 1)`;
    ctx.font = 'bold 7px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('SEAL', cx, y - 2);
    ctx.restore();
  }

  // base plinth
  ctx.fillStyle = '#1d1733';
  roundRect(ctx, x + 3, baseY - 4, TILE - 6, 8, 2);
  ctx.fill();
  // pad — seal pads tint amber so they're visually keyed to the seal door
  const restCol = isSealPad ? `hsl(${SEAL_HUE + 24}, 85%, 60%)` : level.def.accent;
  const col = pressed ? '#5dff9c' : restCol;
  ctx.fillStyle = col;
  ctx.globalAlpha = pressed ? 1 : 0.55 + 0.25 * Math.sin(time * 3.2 + x);
  roundRect(ctx, x + 5, baseY - padH - 3, TILE - 10, padH, 3);
  ctx.fill();
  ctx.globalAlpha = 1;
  // glow ring when pressed
  if (pressed) {
    ctx.strokeStyle = 'rgba(93, 255, 156, 0.5)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x + TILE / 2, baseY - 4, 12 + 3 * Math.sin(time * 6), 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawPortal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  time: number,
  level: ParsedLevel,
  sealed = false,
  armed = false,
): void {
  const cx = x + TILE / 2;
  const cy = y + TILE / 2 - 4;

  // A sealed exit has two unmistakable states. LOCKED: dim, desaturated, slow,
  // red-ward, with a barred core — clearly "not winnable yet". ARMED: the normal
  // portal but supercharged — boosted glow + faster rings + a hot pulse — so the
  // contrast on the open window reads at a glance. Unsealed (legacy) exits use
  // `lit = true, intensity = 1`, i.e. exactly the original look (no change).
  const lit = !sealed || armed;
  // armed seals pulse harder than a plain exit so "GO NOW" is loud
  const pulse = 0.5 + 0.5 * Math.sin(time * (armed ? 9 : 5));
  const intensity = !sealed ? 1 : armed ? 1.15 + 0.25 * pulse : 0.32;
  const accent = lit ? level.def.accent : `hsl(${SEAL_HUE}, 45%, 48%)`;
  const ringSpeed = !sealed ? 1 : armed ? 1.7 : 0.4;

  ctx.save();

  // outer glow — much brighter + larger when armed, a faint ember when locked
  const glowR = 34 * (lit ? (armed ? 1.1 : 1) : 0.7);
  const rg = ctx.createRadialGradient(cx, cy, 2, cx, cy, glowR);
  rg.addColorStop(0, `rgba(255,255,255,${0.35 * intensity})`);
  const hexA = Math.max(0, Math.min(255, Math.round(0x44 * intensity)))
    .toString(16)
    .padStart(2, '0');
  rg.addColorStop(0.4, `${accent}${hexA}`);
  rg.addColorStop(1, 'transparent');
  ctx.fillStyle = rg;
  ctx.fillRect(cx - glowR, cy - glowR, glowR * 2, glowR * 2);

  // counter-rotating rings
  ctx.globalAlpha = lit ? 1 : 0.6;
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2.5;
  ctx.setLineDash([10, 7]);
  ctx.beginPath();
  ctx.arc(cx, cy, 16, time * 1.6 * ringSpeed, time * 1.6 * ringSpeed + Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = lit ? '#ffffff' : `hsl(${SEAL_HUE}, 40%, 65%)`;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([5, 9]);
  ctx.beginPath();
  ctx.arc(cx, cy, 10.5, -time * 2.4 * ringSpeed, -time * 2.4 * ringSpeed + Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;

  // core
  if (lit) {
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = (0.8 + 0.2 * Math.sin(time * 5)) * Math.min(1, intensity);
    ctx.beginPath();
    ctx.arc(cx, cy, 3.5 * (armed ? 1.3 : 1), 0, Math.PI * 2);
    ctx.fill();
  } else {
    // LOCKED core: a dark hub crossed by a padlock bar
    ctx.fillStyle = `hsla(${SEAL_HUE}, 40%, 20%, 0.9)`;
    ctx.beginPath();
    ctx.arc(cx, cy, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = `hsl(${SEAL_HUE}, 50%, 58%)`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy - 1.5, 3, Math.PI, 0); // shackle
    ctx.stroke();
    ctx.fillStyle = `hsl(${SEAL_HUE}, 50%, 50%)`;
    roundRect(ctx, cx - 4, cy - 1.5, 8, 6, 1.5);
    ctx.fill();
  }
  ctx.restore();

  // EXIT beacon — label + bobbing chevron. Sealed exits relabel by state so the
  // text itself tells you whether you can win right now.
  ctx.save();
  const bob = Math.sin(time * 3) * 3;
  const label = !sealed ? 'EXIT' : armed ? 'OPEN!' : 'SEALED';
  const labelCol = lit ? level.def.accent : `hsl(${SEAL_HUE}, 55%, 60%)`;
  ctx.globalAlpha = armed ? 0.7 + 0.3 * pulse : 0.85;
  ctx.fillStyle = labelCol;
  ctx.font = 'bold 10px ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.fillText(label, cx, cy - 34 + bob);
  ctx.strokeStyle = labelCol;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 6, cy - 30 + bob);
  ctx.lineTo(cx, cy - 25 + bob);
  ctx.lineTo(cx + 6, cy - 30 + bob);
  ctx.stroke();
  ctx.restore();
}

// ---- characters --------------------------------------------------------------

function drawRunner(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  face: 1 | -1,
  vy: number,
  grounded: boolean,
  time: number,
  bodyColor: string,
  visorColor: string,
  alpha: number,
): void {
  ctx.save();
  ctx.globalAlpha = alpha;

  // squash & stretch
  let sx = 1;
  let sy = 1;
  if (!grounded) {
    const k = Math.min(1, Math.abs(vy) / 700);
    sy = 1 + k * 0.16;
    sx = 1 - k * 0.12;
  }
  const w = PLAYER_W * sx;
  const h = PLAYER_H * sy;
  const cx = x + PLAYER_W / 2;
  const bottom = y + PLAYER_H;

  // glow
  const rg = ctx.createRadialGradient(cx, bottom - h / 2, 2, cx, bottom - h / 2, 26);
  rg.addColorStop(0, `${visorColor}33`);
  rg.addColorStop(1, 'transparent');
  ctx.fillStyle = rg;
  ctx.fillRect(cx - 26, bottom - h / 2 - 26, 52, 52);

  // body capsule
  ctx.fillStyle = bodyColor;
  roundRect(ctx, cx - w / 2, bottom - h, w, h, 7);
  ctx.fill();

  // visor
  const run = grounded ? Math.sin(time * 14) * 1.2 : 0;
  ctx.fillStyle = visorColor;
  const vw = w * 0.42;
  const vx = face === 1 ? cx + w / 2 - vw - 2.5 : cx - w / 2 + 2.5;
  roundRect(ctx, vx, bottom - h + 5.5 + run * 0.4, vw, 4.5, 2);
  ctx.fill();

  // feet accents
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  roundRect(ctx, cx - w / 2 + 2, bottom - 4, w / 2 - 3, 3, 1.5);
  ctx.fill();
  roundRect(ctx, cx + 1, bottom - 4, w / 2 - 3, 3, 1.5);
  ctx.fill();

  ctx.restore();
}

function drawEcho(ctx: CanvasRenderingContext2D, sim: Sim, e: Echo, time: number): void {
  const f = sim.echoFrame(e);
  const frozen = sim.echoFrozen(e);
  const col = `hsl(${e.hue}, 95%, 68%)`;
  const flicker = frozen ? 0.55 : 0.5 + 0.12 * Math.sin(time * 22 + e.id * 3);

  // anchor ring once frozen in place
  if (frozen) {
    ctx.strokeStyle = `hsla(${e.hue}, 90%, 70%, ${0.3 + 0.15 * Math.sin(time * 3)})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(f.x + PLAYER_W / 2, f.y + PLAYER_H, 15, 4.5, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  drawRunner(ctx, f.x, f.y, f.face, 0, f.grounded, time, col, '#ffffff', flicker);

  // scanline mask over the echo body
  ctx.save();
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = '#0a0613';
  const top = Math.floor(f.y);
  for (let yy = top; yy < top + PLAYER_H; yy += 3) {
    ctx.fillRect(f.x - 2, yy + ((time * 18) % 3), PLAYER_W + 4, 1);
  }
  ctx.restore();

  // id badge
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = col;
  ctx.font = 'bold 9px ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.fillText(String(e.id), f.x + PLAYER_W / 2, f.y - 5);
  ctx.globalAlpha = 1;
}

export function drawPlayerTrail(
  ctx: CanvasRenderingContext2D,
  trail: TrailPoint[],
  camX: number,
  color: string,
): void {
  ctx.save();
  ctx.translate(-camX, 0);
  for (const t of trail) {
    const a = Math.max(0, 0.16 - t.age * 0.5);
    if (a <= 0) continue;
    ctx.globalAlpha = a;
    ctx.fillStyle = color;
    roundRect(ctx, t.x, t.y, PLAYER_W, PLAYER_H, 7);
    ctx.fill();
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

// ---- post / overlays -----------------------------------------------------------

export function drawPost(ctx: CanvasRenderingContext2D): void {
  // scanlines
  ctx.save();
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = '#000';
  for (let y = 0; y < VIEW_H; y += 3) ctx.fillRect(0, y, VIEW_W, 1);
  ctx.restore();
  // vignette
  const vg = ctx.createRadialGradient(
    VIEW_W / 2, VIEW_H / 2, VIEW_H * 0.45,
    VIEW_W / 2, VIEW_H / 2, VIEW_H * 0.95,
  );
  vg.addColorStop(0, 'transparent');
  vg.addColorStop(1, 'rgba(2, 0, 8, 0.55)');
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);
}
